import { useCallback, useState } from 'react'
import * as yup from 'yup'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  AGENDA_DATE_START,
  AGENDA_DATE_END,
  AGENDA_TASK_ASSIGNMENT,
  AGENDA_TASK_CREATION,
  AGENDA_TASK_PUBLICATION,
} from 'lib/agenda'

import {
  isStepDisabled,
  SESSIONS_STEPS_LIST,
  SESSIONS_STEP_COMPLETED,
  SESSIONS_STEP_COMPLETED_WITH_ERRORS,
  SESSIONS_STEP_CONFIRMATION,
  SESSIONS_STEP_DISABLED,
  SESSIONS_STEP_OPEN,
  SESSIONS_STEP_PLANNING,
  SESSIONS_STEP_PRICING,
} from './sessionsLib'

const useSessionSteps = () => {
  const { t } = useTranslation()

  const [state, setState] = useState({
    session: {},
    pricingStepCars: [],
    steps: [],
  })

  const handlePlanningValidation = useCallback(
    (session) => {
      const tasksDates = [
        `${AGENDA_TASK_CREATION}.${AGENDA_DATE_START}`,
        `${AGENDA_TASK_CREATION}.${AGENDA_DATE_END}`,
        `${AGENDA_TASK_PUBLICATION}.${AGENDA_DATE_START}`,
        `${AGENDA_TASK_PUBLICATION}.${AGENDA_DATE_END}`,
        `${AGENDA_TASK_ASSIGNMENT}.${AGENDA_DATE_START}`,
        `${AGENDA_TASK_ASSIGNMENT}.${AGENDA_DATE_END}`,
      ]

      const errors = tasksDates
        .map((taskDate) =>
          yup.date().isValidSync(_.get(session.agenda, taskDate))
            ? null
            : { id: taskDate, error: t('common.date.error.invalidFormat') }
        )
        .filter((v) => v)

      if (errors.length) {
        return errors
      }

      for (let i = 1; i < tasksDates.length; i++) {
        if (
          _.get(session.agenda, tasksDates[i]) <=
          _.get(session.agenda, tasksDates[i - 1])
        ) {
          errors.push({
            id: tasksDates[i],
            error: t('common.date.error.isNotAfter', {
              attribute: t(`sessions.planning.${tasksDates[i - 1]}`),
            }),
          })
          return errors
        }
      }
      return errors
    },
    [t]
  )

  const handleStepValidation = useCallback(
    (step, session) => {
      if (!session) {
        session = state.session
      }
      if (step === SESSIONS_STEP_PLANNING) {
        return handlePlanningValidation(session)
      }
      if (step === SESSIONS_STEP_PRICING) {
        return (
          session &&
          state.pricingStepCars.length &&
          state.pricingStepCars.every(
            (car) =>
              session.requiredGroups &&
              !session.requiredGroups
                .filter((group) => group)
                .some((group) => !car.prices?.[group])
          )
        )
      }

      if (step === SESSIONS_STEP_CONFIRMATION) {
        return true
      }
      return false
    },
    [handlePlanningValidation, state.pricingStepCars, state.session]
  )

  const _isCompletedWithErrors = useCallback(
    (step, state) =>
      step === SESSIONS_STEP_PLANNING
        ? handleStepValidation(step, state.session).length
        : [SESSIONS_STEP_PRICING].includes(step)
        ? state[`${step}StepCars`].some(
            (car) => car[`existsIn${step[0].toUpperCase() + step.slice(1)}`]
          ) && !handleStepValidation(step, state.session)
        : false,
    [handleStepValidation]
  )

  const getStepsWithState = useCallback(() => {
    if (!Object.keys(state.session).length) return []
    return SESSIONS_STEPS_LIST.map((step) => {
      const result = {
        step,
        label: t(`sessions.${step}.step`),
        state: SESSIONS_STEP_OPEN,
      }
      if (isStepDisabled(state.session, step)) {
        result.state = SESSIONS_STEP_DISABLED
        return result
      }
      if (state.session[`${step}AcceptedAt`]) {
        result.state = SESSIONS_STEP_COMPLETED
        return result
      }

      if (_isCompletedWithErrors(step, state)) {
        result.state = SESSIONS_STEP_COMPLETED_WITH_ERRORS
        return result
      }
      return result
    })
  }, [_isCompletedWithErrors, state, t])

  const init = useCallback((session) => {
    setState((s) => ({
      ...s,
      session,
      pricingStepCars: session.cars,
    }))
  }, [])

  return {
    pricingStepCars: state.pricingStepCars,
    steps: state.steps,
    initSteps: init,
    handleStepValidation,
    getStepsWithState,
  }
}

export default useSessionSteps
